import { getErrorFromAPI } from '@/helpers/toast';
import { useAutoTranslateLanguageMutation } from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import { languagesActions } from '@/redux/features/languages.slice';
import { TranslationSections } from '@/types/language';
import { BlockStack, Modal, Text } from '@shopify/polaris';
import { useDispatch } from 'react-redux';

interface Props {
  open: boolean;
  onClose: () => void;
  language: string;
  currentTarget: TranslationSections;
}

const AutoTranslateModal = ({ open, onClose, language, currentTarget }: Props) => {
  const dispatch = useDispatch();
  const [autoTranslate, { isLoading }] = useAutoTranslateLanguageMutation();

  const onConfirm = async () => {
    // Send the current (possibly unsaved) editor draft so fields the merchant just cleared count as empty.
    const res: any = await autoTranslate({ language, ...currentTarget });
    if ('data' in res && res.data && res.data?.state === 1) {
      const filled: TranslationSections = res.data.data || {};
      // Only merge fields whose value actually changed, so they show as dirty for review.
      const changed: TranslationSections = {};
      for (const section of Object.keys(filled)) {
        for (const key of Object.keys(filled[section] || {})) {
          const value = filled[section][key];
          if (value && (currentTarget[section]?.[key] ?? '') !== value) {
            changed[section] = changed[section] || {};
            changed[section][key] = value;
          }
        }
      }
      if (Object.keys(changed).length) dispatch(languagesActions.mergeAutoTranslated(changed));
      dispatch(commonSlice.actions.handleToast({ isOpen: true, content: 'Auto-translated empty fields', error: false }));
      onClose();
    } else {
      dispatch(commonSlice.actions.handleToast({ isOpen: true, content: getErrorFromAPI(res), error: true }));
    }
  };

  return (
    <Modal
      open={open}
      title="Auto-translate empty fields?"
      onClose={onClose}
      primaryAction={{ content: 'Auto-translate', onAction: onConfirm, loading: isLoading }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd">
            Only empty fields are filled in. Existing translations are left unchanged, and placeholders like{' '}
            {'{tracking_number}'} stay intact.
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            Review the filled values and click “Save and publish” to keep them.
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default AutoTranslateModal;
